import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { Container, Card, Form, Button, Spinner, Alert, Table, Modal, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';

// カラーパレットの定義
const colorPalette = [
  { name: '黒', value: '#000000' },
  { name: '赤', value: '#dc3545' },
  { name: '青', value: '#0d6efd' },
  { name: '緑', value: '#198754' },
  { name: '黄', value: '#ffc107' },
  { name: 'グレー', value: '#6c757d' },
];

// 【新規追加】クリック可能なカラーピッカーコンポーネント
const ColorPicker = ({ name, value, onChange }) => {
  return (
    <div className="d-flex align-items-center flex-wrap">
      {colorPalette.map(color => (
        <div
          key={color.value}
          title={color.name}
          onClick={() => onChange({ target: { name, value: color.value } })}
          style={{
            backgroundColor: color.value,
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            margin: '4px',
            cursor: 'pointer',
            border: value === color.value ? '3px solid #0d6efd' : '1px solid #dee2e6',
            boxShadow: value === color.value ? '0 0 0 1px white' : 'none',
            outline: value === color.value ? `2px solid ${color.value}` : 'none',
          }}
        />
      ))}
    </div>
  );
};


function NewsAdmin() {
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newNews, setNewNews] = useState({
    title: '',
    content: '',
    startDate: new Date().toISOString().slice(0, 10),
    endDate: '',
    status: 'published',
    targetAudience: { '一般ユーザー': true, '社員': true, '出荷担当者': true, '管理者': true },
    titleColor: '#000000',
    contentColor: '#000000',
    linkUrl: '',
    linkText: '',
  });
  const [editingNews, setEditingNews] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const newsCollectionRef = collection(db, 'news');

  const fetchNews = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getDocs(newsCollectionRef);
      const fetchedNews = data.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      fetchedNews.sort((a, b) => b.startDate.toDate() - a.startDate.toDate());
      setNewsList(fetchedNews);
    } catch (err) {
      setError('ニュースの取得に失敗しました。');
      console.error(err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    document.title = 'ニュース管理 | My Order System';
    fetchNews();
  }, [fetchNews]);

  const handleNewNewsChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith('targetAudience.')) {
      const audience = name.split('.')[1];
      setNewNews(prev => ({ ...prev, targetAudience: { ...prev.targetAudience, [audience]: checked } }));
    } else {
      setNewNews(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleAddNews = async () => {
    if (!newNews.title || !newNews.content) {
      alert('タイトルと内容は必須です。');
      return;
    }
    try {
      const newsData = {
        ...newNews,
        startDate: Timestamp.fromDate(new Date(newNews.startDate)),
        endDate: newNews.endDate ? Timestamp.fromDate(new Date(newNews.endDate)) : null,
        createdAt: Timestamp.now(),
      };
      await addDoc(newsCollectionRef, newsData);
      fetchNews();
      setNewNews({
        title: '', content: '', startDate: new Date().toISOString().slice(0, 10), endDate: '',
        status: 'published', targetAudience: { '一般ユーザー': true, '社員': true, '出荷担当者': true, '管理者': true },
        titleColor: '#000000', contentColor: '#000000', linkUrl: '', linkText: '',
      });
    } catch (err) {
      alert('ニュースの追加に失敗しました。');
      console.error(err);
    }
  };

  const handleShowEditModal = (news) => {
    setEditingNews({
      ...news,
      startDate: news.startDate.toDate().toISOString().slice(0, 10),
      endDate: news.endDate ? news.endDate.toDate().toISOString().slice(0, 10) : '',
    });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingNews(null);
  };

  const handleEditNewsChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith('targetAudience.')) {
      const audience = name.split('.')[1];
      setEditingNews(prev => ({ ...prev, targetAudience: { ...prev.targetAudience, [audience]: checked } }));
    } else {
      setEditingNews(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleUpdateNews = async () => {
    if (!editingNews) return;
    try {
      const newsDoc = doc(db, 'news', editingNews.id);
      const { id, ...dataToUpdate } = editingNews;
      await updateDoc(newsDoc, {
        ...dataToUpdate,
        startDate: Timestamp.fromDate(new Date(editingNews.startDate)),
        endDate: editingNews.endDate ? Timestamp.fromDate(new Date(editingNews.endDate)) : null,
      });
      handleCloseEditModal();
      fetchNews();
    } catch (err) {
      alert('ニュースの更新に失敗しました。');
      console.error(err);
    }
  };

  const handleDeleteNews = async (id) => {
    if (window.confirm('このニュースを本当に削除しますか？')) {
      try {
        const newsDoc = doc(db, 'news', id);
        await deleteDoc(newsDoc);
        fetchNews();
      } catch (err) {
        alert('ニュースの削除に失敗しました。');
        console.error(err);
      }
    }
  };

  return (
    <main>
      <Container className="my-4">
        <Card className="shadow-sm">
          <Card.Header as="h2" className="p-3">
            お知らせ管理
          </Card.Header>
          <Card.Body className="p-4">
            {
              error &&
              <Alert variant="danger">
                {error}
              </Alert>
            }
            <Card className="mb-4">
              <Card.Header>
                新規ニュース登録
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>
                    タイトル
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={newNews.title}
                    onChange={handleNewNewsChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>
                    内容
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="content"
                    value={newNews.content}
                    onChange={handleNewNewsChange}
                  />
                </Form.Group>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        リンクURL (任意)
                      </Form.Label>
                      <Form.Control
                        type="url"
                        name="linkUrl"
                        value={newNews.linkUrl}
                        onChange={handleNewNewsChange}
                        placeholder="https://example.com"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        リンクテキスト (任意)
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="linkText"
                        value={newNews.linkText}
                        onChange={handleNewNewsChange}
                        placeholder="詳細はこちら"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        掲載開始日
                      </Form.Label>
                      <Form.Control
                        type="date"
                        name="startDate"
                        value={newNews.startDate}
                        onChange={handleNewNewsChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        掲載終了日 (任意)
                      </Form.Label>
                      <Form.Control
                        type="date"
                        name="endDate"
                        value={newNews.endDate}
                        onChange={handleNewNewsChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                {/* 【変更点】カラー選択をColorPickerコンポーネントに変更 */}
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        タイトル色
                      </Form.Label>
                      <ColorPicker
                        name="titleColor"
                        value={newNews.titleColor}
                        onChange={handleNewNewsChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        内容色
                      </Form.Label>
                      <ColorPicker
                        name="contentColor"
                        value={newNews.contentColor}
                        onChange={handleNewNewsChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label>
                    公開ステータス
                  </Form.Label>
                  <Form.Select
                    name="status"
                    value={newNews.status}
                    onChange={handleNewNewsChange}
                  >
                    <option value="published">
                      公開
                    </option>
                    <option value="draft">
                      下書き
                    </option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>
                    対象ユーザー
                  </Form.Label>
                  <div>
                    {Object.keys(newNews.targetAudience).map(type =>
                    (
                      <Form.Check
                        inline
                        key={type}
                        type="checkbox"
                        id={`new-${type}`}
                        label={type}
                        name={`targetAudience.${type}`}
                        checked={newNews.targetAudience[type]}
                        onChange={handleNewNewsChange}
                      />
                    )
                    )}
                  </div>
                </Form.Group>
                <Button variant="primary" onClick={handleAddNews}>
                  登録
                </Button>
              </Card.Body>
            </Card>

            <h3>
              登録済みニュース一覧
            </h3>
            {
              loading ?
                <div className="text-center">
                  <Spinner animation="border" />
                </div>
                :
                (
                  <Table
                    striped
                    bordered
                    hover
                    responsive
                  >
                    <thead>
                      <tr>
                        <th>
                          タイトル
                        </th>
                        <th>
                          ステータス
                        </th>
                        <th>
                          掲載開始日
                        </th>
                        <th>
                          掲載終了日
                        </th>
                        <th>
                          操作
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {newsList.map(news => (
                        <tr key={news.id}>
                          <td>
                            {news.title}
                          </td>
                          <td>
                            <span
                              className={
                                `badge 
                              ${news.status === 'published' ?
                                  'bg-success'
                                  :
                                  'bg-secondary'
                                }`
                              }
                            >
                              {news.status}
                            </span>
                          </td>
                          <td>
                            {news.startDate.toDate().toLocaleDateString()}
                          </td>
                          <td>
                            {news.endDate ? news.endDate.toDate().toLocaleDateString() : 'なし'}
                          </td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleShowEditModal(news)}
                            >
                              編集
                            </Button>
                            {' '}
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteNews(news.id)}
                            >
                              削除
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
            <hr className="my-4" />
            <Link to="/admin">
              管理者メニューに戻る
            </Link>
          </Card.Body>
        </Card>

        <Modal
          show={showEditModal}
          onHide={handleCloseEditModal} size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              ニュース編集
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {
              editingNews &&
              (
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      タイトル
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={editingNews.title}
                      onChange={handleEditNewsChange}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      内容
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="content"
                      value={editingNews.content}
                      onChange={handleEditNewsChange}
                    />
                  </Form.Group>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          リンクURL (任意)
                        </Form.Label>
                        <Form.Control
                          type="url"
                          name="linkUrl"
                          value={editingNews.linkUrl}
                          onChange={handleEditNewsChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          リンクテキスト (任意)
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="linkText"
                          value={editingNews.linkText}
                          onChange={handleEditNewsChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          掲載開始日
                        </Form.Label>
                        <Form.Control
                          type="date"
                          name="startDate"
                          value={editingNews.startDate}
                          onChange={handleEditNewsChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          掲載終了日 (任意)
                        </Form.Label>
                        <Form.Control
                          type="date"
                          name="endDate"
                          value={editingNews.endDate}
                          onChange={handleEditNewsChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  {/* 【変更点】編集モーダルでもカラー選択をColorPickerコンポーネントに変更 */}
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          タイトル色
                        </Form.Label>
                        <ColorPicker
                          name="titleColor"
                          value={editingNews.titleColor}
                          onChange={handleEditNewsChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          内容色
                        </Form.Label>
                        <ColorPicker
                          name="contentColor"
                          value={editingNews.contentColor}
                          onChange={handleEditNewsChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      公開ステータス
                    </Form.Label>
                    <Form.Select
                      name="status"
                      value={editingNews.status}
                      onChange={handleEditNewsChange}
                    >
                      <option value="published">
                        公開
                      </option>
                      <option value="draft">
                        下書き
                      </option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      対象ユーザー
                    </Form.Label>
                    <div>
                      {Object.keys(editingNews.targetAudience).map(type => (
                        <Form.Check
                          inline
                          key={type}
                          type="checkbox"
                          id={`edit-${type}`}
                          label={type}
                          name={`targetAudience.${type}`}
                          checked={editingNews.targetAudience[type]}
                          onChange={handleEditNewsChange}
                        />
                      ))}
                    </div>
                  </Form.Group>
                </Form>
              )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseEditModal}>
              キャンセル
            </Button>
            <Button variant="primary" onClick={handleUpdateNews}>
              保存
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </main>
  );
}

export default NewsAdmin;
